import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../utils/api'
import styles from './EmployeeFormPage.module.css'

interface EmployeeForm {
  firstName: string
  lastName: string
  birthDate: string
  nationality: string
  gender: string
  addressStreet: string
  addressCity: string
  addressPostal: string
  addressCountry: string
  email: string
  phone: string
  iban: string
  bic: string
  taxClass: string
  taxCardNumber: string
  rtsPersonalRate: string
  matriculeCcss: string
  grossMonthlySalary: string
  weeklyHours: string
  entryDate: string
  exitDate: string
  department: string
  position: string
}

const EMPTY_FORM: EmployeeForm = {
  firstName: '', lastName: '', birthDate: '', nationality: 'LU', gender: '',
  addressStreet: '', addressCity: '', addressPostal: '', addressCountry: 'LU',
  email: '', phone: '', iban: '', bic: '',
  taxClass: '1', taxCardNumber: '', rtsPersonalRate: '', matriculeCcss: '',
  grossMonthlySalary: '', weeklyHours: '40',
  entryDate: '', exitDate: '', department: '', position: '',
}

export function EmployeeFormPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = id && id !== 'new'

  const [form, setForm] = useState<EmployeeForm>(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(Boolean(isEdit))

  useEffect(() => {
    if (!isEdit) return
    apiFetch<{ employee: Record<string, unknown> }>(`/employees/${id}`).then(res => {
      if (res.data) {
        const e = res.data.employee
        const addr = (e['address'] ?? {}) as Record<string, string>
        const s = (k: string) => (e[k] as string) ?? ''
        setForm({
          firstName: s('firstName'),
          lastName: s('lastName'),
          birthDate: s('birthDate'),
          nationality: (e['nationality'] as string) ?? 'LU',
          gender: s('gender'),
          addressStreet: addr['street'] ?? '',
          addressCity: addr['city'] ?? '',
          addressPostal: addr['postalCode'] ?? '',
          addressCountry: addr['country'] ?? 'LU',
          email: s('email'),
          phone: s('phone'),
          iban: s('iban'),
          bic: s('bic'),
          taxClass: (e['taxClass'] as string) ?? '1',
          taxCardNumber: s('taxCardNumber'),
          rtsPersonalRate: e['rtsPersonalRate'] != null ? String(e['rtsPersonalRate']) : '',
          matriculeCcss: s('matriculeCcss'),
          grossMonthlySalary: e['grossMonthlySalary'] != null ? String(Number(e['grossMonthlySalary']) / 100) : '',
          weeklyHours: e['weeklyHours'] != null ? String(e['weeklyHours']) : '40',
          entryDate: s('entryDate'),
          exitDate: s('exitDate'),
          department: s('department'),
          position: s('position'),
        })
      }
      setLoading(false)
    })
  }, [id, isEdit])

  const update = (key: keyof EmployeeForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    // Convert salary EUR to cents, parse numeric fields
    const payload = {
      ...form,
      grossMonthlySalary: form.grossMonthlySalary ? Math.round(parseFloat(form.grossMonthlySalary) * 100) : null,
      rtsPersonalRate: form.rtsPersonalRate ? parseFloat(form.rtsPersonalRate) : null,
      weeklyHours: form.weeklyHours ? parseFloat(form.weeklyHours) : 40,
    }

    const res = isEdit
      ? await apiFetch(`/employees/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
      : await apiFetch<{ id: string }>('/employees', { method: 'POST', body: JSON.stringify(payload) })

    if (res.error) {
      setError(res.error)
      setSubmitting(false)
      return
    }

    navigate('/employees')
  }

  if (loading) return <p>{t('common.loading')}</p>

  return (
    <div className={styles.page}>
      <h1>{isEdit ? t('employees.edit_title') : t('employees.create_title')}</h1>

      {error && <div className={styles.error}>{error}</div>}

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* Identite */}
        <fieldset className={styles.fieldset}>
          <legend>{t('employees.section_identity')}</legend>
          <div className={styles.row2}>
            <Field label={t('employees.first_name')} value={form.firstName} onChange={v => update('firstName', v)} required />
            <Field label={t('employees.last_name')} value={form.lastName} onChange={v => update('lastName', v)} required />
          </div>
          <div className={styles.row3}>
            <Field label={t('employees.birth_date')} value={form.birthDate} onChange={v => update('birthDate', v)} type="date" />
            <Field label={t('employees.nationality')} value={form.nationality} onChange={v => update('nationality', v)} />
            <SelectField label={t('employees.gender')} value={form.gender} onChange={v => update('gender', v)}
              options={[
                { value: '', label: '-' },
                { value: 'M', label: t('employees.gender_m') },
                { value: 'F', label: t('employees.gender_f') },
              ]}
            />
          </div>
        </fieldset>

        {/* Adresse */}
        <fieldset className={styles.fieldset}>
          <legend>{t('employees.section_address')}</legend>
          <Field label={t('employees.street')} value={form.addressStreet} onChange={v => update('addressStreet', v)} />
          <div className={styles.row3}>
            <Field label={t('employees.postal')} value={form.addressPostal} onChange={v => update('addressPostal', v)} />
            <Field label={t('employees.city')} value={form.addressCity} onChange={v => update('addressCity', v)} />
            <Field label={t('employees.country')} value={form.addressCountry} onChange={v => update('addressCountry', v)} />
          </div>
        </fieldset>

        {/* Contact */}
        <fieldset className={styles.fieldset}>
          <legend>{t('employees.section_contact')}</legend>
          <div className={styles.row2}>
            <Field label={t('auth.email')} value={form.email} onChange={v => update('email', v)} type="email" />
            <Field label={t('employees.phone')} value={form.phone} onChange={v => update('phone', v)} type="tel" />
          </div>
        </fieldset>

        {/* Bancaire */}
        <fieldset className={styles.fieldset}>
          <legend>{t('employees.section_bank')}</legend>
          <div className={styles.row2}>
            <Field label="IBAN" value={form.iban} onChange={v => update('iban', v)} />
            <Field label="BIC" value={form.bic} onChange={v => update('bic', v)} />
          </div>
        </fieldset>

        {/* Fiscal + Remuneration */}
        <fieldset className={styles.fieldset}>
          <legend>{t('employees.section_fiscal')}</legend>
          <div className={styles.row3}>
            <SelectField label={t('employees.tax_class')} value={form.taxClass} onChange={v => update('taxClass', v)}
              options={[
                { value: '1', label: t('simulator.class_1') },
                { value: '1a', label: t('simulator.class_1a') },
                { value: '2', label: t('simulator.class_2') },
              ]}
            />
            <Field label={t('employees.tax_card')} value={form.taxCardNumber} onChange={v => update('taxCardNumber', v)} />
            <Field label={t('employees.rts_rate')} value={form.rtsPersonalRate} onChange={v => update('rtsPersonalRate', v)} type="number" />
          </div>
          <div className={styles.row3}>
            <Field label={t('employees.matricule')} value={form.matriculeCcss} onChange={v => update('matriculeCcss', v)} />
            <Field label={t('employees.gross_salary')} value={form.grossMonthlySalary} onChange={v => update('grossMonthlySalary', v)} type="number" />
            <Field label={t('employees.weekly_hours')} value={form.weeklyHours} onChange={v => update('weeklyHours', v)} type="number" />
          </div>
        </fieldset>

        {/* Emploi */}
        <fieldset className={styles.fieldset}>
          <legend>{t('employees.section_employment')}</legend>
          <div className={styles.row2}>
            <Field label={t('employees.entry_date')} value={form.entryDate} onChange={v => update('entryDate', v)} type="date" required />
            {isEdit && <Field label={t('employees.exit_date')} value={form.exitDate} onChange={v => update('exitDate', v)} type="date" />}
          </div>
          <div className={styles.row2}>
            <Field label={t('employees.department')} value={form.department} onChange={v => update('department', v)} />
            <Field label={t('employees.position')} value={form.position} onChange={v => update('position', v)} />
          </div>
        </fieldset>

        {/* Actions */}
        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={() => navigate('/employees')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className={styles.submitBtn} disabled={submitting}>
            {submitting ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </div>
  )
}

// ─── Field sub-components ────────────────────────────────────────

function Field({ label, value, onChange, type = 'text', required }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}{required && ' *'}</span>
      <input
        type={type}
        className={styles.input}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
      />
    </label>
  )
}

function SelectField({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void
  options: Array<{ value: string; label: string }>
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <select className={styles.input} value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}
